const NO_OPAQUE_FIELDS: ReadonlySet<string> = new Set();

/**
 * Recursively convert object keys from camelCase to snake_case for the Remi
 * API. Arrays are mapped element-by-element; primitives pass through.
 *
 * `opaqueMapFields` names fields whose VALUE is a user-authored map — their
 * inner keys are display text, not schema fields, so they're copied verbatim
 * instead of being snake-cased. Pass an empty set (the default) for plain
 * snake-case conversion.
 */
export const toSnakeCaseKeys = (
  data: unknown,
  opaqueMapFields: ReadonlySet<string> = NO_OPAQUE_FIELDS
): unknown => {
  if (Array.isArray(data)) {
    return data.map((item) => toSnakeCaseKeys(item, opaqueMapFields));
  }

  if (data !== null && typeof data === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      // Opaque map fields keep their (user-authored) inner keys verbatim.
      result[snakeKey] = opaqueMapFields.has(snakeKey)
        ? value
        : toSnakeCaseKeys(value, opaqueMapFields);
    }
    return result;
  }

  return data;
};

/**
 * Fields on the org payload whose keys are user-authored free text and must
 * NOT be snake-cased.
 *
 * Only `org_links` qualifies: its keys are compliance-link display titles
 * (SPEC-2239 — snake-casing "Privacy Policy" → "privacy_policy" corrupted the
 * title shown on the property page). Do NOT add other org fields here unless
 * their KEYS are user-authored text:
 *   - `settings` is a fixed-schema object ({ flywheel_enabled }) — its keys
 *     must be snake-cased.
 *   - `onboardingData` is already snake_case; its user text lives in values
 *     (and in `compliance_links` array items keyed by `key`/`value`), not in
 *     object keys, so snake-casing it is safe/idempotent.
 */
const ORG_OPAQUE_MAP_FIELDS: ReadonlySet<string> = new Set(['org_links']);

/**
 * Convert an organization payload to the API's snake_case schema while
 * preserving user-authored compliance-link titles stored as `org_links` keys.
 */
export const mapOrganizationToApi = (data: unknown): unknown =>
  toSnakeCaseKeys(data, ORG_OPAQUE_MAP_FIELDS);
